import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import apis from '../../../utils/apis';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface AdminRoleDetailModalProps {
  roleId: string | number;
  onConfirm: (userId: string) => void;
  onCancel: () => void;
}

const AdminRoleDetailModal: React.FC<AdminRoleDetailModalProps> = ({
  roleId,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, [roleId]);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoadingUsers(true);
      const res = await apis.get('/admin/users');
      if (res?.data?.success && res?.data?.data) {
        setUsers(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const handleAssignRole = async () => {
    if (!selectedUserId) return;

    try {
      setIsAssigning(true);
      onConfirm(selectedUserId);
    } catch (err) {
      console.error('Error assigning role:', err);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="min-w-[400px] sm:w-full">
      <div className="px-6 py-4 space-y-4">
        {isLoadingUsers ? (
          <div className="text-center py-4 text-fg-muted">{t('Loading users...')}</div>
        ) : (
          <Dropdown
            label={t('User')}
            options={users.map((user) => ({
              value: String(user.id),
              label: `${user.first_name} ${user.last_name} (${user.email})`,
            }))}
            value={selectedUserId}
            onChange={(value) => {
              setSelectedUserId(Array.isArray(value) ? value[0] || '' : value || '');
            }}
            fullWidth
          />
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex gap-3 justify-end px-6 py-4">
        <Button
          variant="neutral"
          size="small"
          onClick={onCancel}
          disabled={isAssigning}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleAssignRole}
          disabled={isAssigning || !selectedUserId || isLoadingUsers}
        >
          {isAssigning ? t('Assigning...') : t('Assign')}
        </Button>
      </div>
    </div>
  );
};

export default AdminRoleDetailModal;
